import { useEffect, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import type { SearchScope } from "@/features/search/searchScope";
import { cn } from "@/shared/lib";
import { Input } from "@/shared/ui/form";

export const MAX_ARCHIVE_SEARCH_QUERY_LENGTH = 100;

// biome-ignore lint/style/useComponentExportOnlyModules: URL/APIと同じ正規化を直接テストする
export function normalizeArchiveSearchQuery(value: string): string {
  return value.trim().slice(0, MAX_ARCHIVE_SEARCH_QUERY_LENGTH);
}

interface ArchiveSearchBarProps {
  className?: string;
  isFetching: boolean;
  query: string;
  scope: SearchScope;
  onClear: () => void;
  onScopeChange: (scope: SearchScope) => void;
  onSubmit: (query: string) => void;
}

/** URLを正本にする過去ログ専用の検索欄。 */
export const ArchiveSearchBar: React.FunctionComponent<
  ArchiveSearchBarProps
> = ({
  className,
  isFetching,
  query,
  scope,
  onClear,
  onScopeChange,
  onSubmit,
}: ArchiveSearchBarProps) => {
  const [value, setValue] = useState(query);

  useEffect(() => {
    setValue(query);
  }, [query]);

  const handleClear = (): void => {
    setValue("");
    onClear();
  };

  return (
    <search
      aria-label="過去ログ検索"
      className={cn("w-full md:w-auto", className)}
    >
      <form
        className="catalog-search-form flex w-full items-center gap-2"
        onSubmit={(event: React.SubmitEvent<HTMLFormElement>): void => {
          event.preventDefault();
          const normalizedQuery = normalizeArchiveSearchQuery(value);
          if (normalizedQuery) {
            onSubmit(normalizedQuery);
          } else if (query) {
            onClear();
          }
        }}
      >
        <SearchScopeSelect
          ariaLabel="過去ログ検索の検索モード"
          scope={scope}
          onChange={onScopeChange}
        />
        <div
          className="catalog-search-field relative flex min-w-0 flex-1 items-center"
          data-has-query={value ? "true" : "false"}
        >
          <Input
            type="search"
            value={value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              setValue(event.currentTarget.value)
            }
            placeholder="過去ログ検索"
            aria-label="過去ログ検索"
            maxLength={MAX_ARCHIVE_SEARCH_QUERY_LENGTH}
            className={`archive-search-input catalog-search-input min-h-11 min-w-0 flex-1 ${
              value ? "pr-24" : "pr-12"
            }`}
          />
          <fieldset
            aria-label="過去ログ検索操作"
            className="absolute inset-y-0 right-0 m-0 flex min-w-0 items-center border-0 p-0"
          >
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                aria-label="過去ログ検索をクリア"
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            )}
            <button
              type="submit"
              disabled={isFetching || (!value.trim() && !query)}
              className={cn(
                "catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50",
                value && "catalog-search-action-divider",
              )}
              aria-label="過去ログを検索"
            >
              <FiSearch size={18} aria-hidden="true" />
            </button>
          </fieldset>
        </div>
      </form>
    </search>
  );
};
